import { useEffect, useState } from "react";
import { get } from "../api";
import type { CustomerPace, PaceState } from "../api";
import { stoppedSince } from "./PacePanel";
import { dayLabel, money, signedMoney, signedPct } from "./format";

const STATE_LABEL: Record<PaceState, string> = {
  behind: "Behind",
  on_pace: "On pace",
  ahead: "Ahead",
  new: "New",
  stopped: "Stopped",
};

/**
 * One customer's month so far, campaign by campaign, against the same days
 * of last month. The current month's view: it has no brief until it freezes.
 */
export default function PaceCard({ slug }: { slug: string }) {
  const [data, setData] = useState<CustomerPace | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [all, setAll] = useState(false);

  useEffect(() => {
    setData(undefined);
    setError(null);
    void get<CustomerPace | null>(`/api/customers/${slug}/revenue/pace`)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [slug]);

  if (error) return <div className="error">{error}</div>;
  if (data === undefined) return null;
  if (data === null) {
    return (
      <div className="rev-brief">
        <strong>This month so far</strong>
        <p className="dim">Nothing billed this month or last to compare.</p>
      </div>
    );
  }

  const c = data.currency;
  const moving = data.campaigns.filter((x) => x.state !== "on_pace");
  const shown = all ? data.campaigns : moving.slice(0, 8);

  return (
    <div className="rev-brief">
      <div className="rev-brief-head">
        <strong>This month so far</strong>
        <span className={`badge pace ${data.state}`}>{STATE_LABEL[data.state]}</span>
        <span className="dim">
          1–{dayLabel(data.as_of)} vs the same {data.days_compared} days of last month
        </span>
      </div>
      <p className="rev-headline">
        {money(data.mtd, c)} so far against {money(data.prior_mtd, c)} at this point last month{" "}
        <span className={Number(data.delta) < 0 ? "neg" : "pos"}>
          {signedMoney(data.delta, c)} ({signedPct(data.delta_pct)})
        </span>
        {data.projected_total !== null && (
          <span className="dim">
            {" "}
            · run-rate {money(data.projected_total, c)}
            {data.prior_month !== null && ` vs ${money(data.prior_month, c)} last month`}
          </span>
        )}
      </p>
      {data.too_early && <p className="dim">Too early in the month to flag anything.</p>}
      {data.estimated && (
        <p className="dim">No billing plan in effect: priced at the default rate, so tier and commitment are not checked.</p>
      )}
      {data.mmc_at_risk !== null && (
        <p className="neg">
          Heading {Math.round(Number(data.mmc_at_risk)).toLocaleString("en-IN")} minutes below the minimum
          commitment, which it cleared last month.
        </p>
      )}
      {data.tier_change && (
        <p className={data.tier_change.down ? "neg" : "pos"}>
          On pace for {data.tier_change.to}; last month was {data.tier_change.from}.
        </p>
      )}

      {data.campaigns.length > 0 && (
        <table className="rev-pace-table">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>So far</th>
              <th>Same days last month</th>
              <th>Change</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((x) => (
              <tr key={x.campaign_id}>
                <td>{x.name}</td>
                <td>{money(x.mtd, c)}</td>
                <td>{money(x.prior_mtd, c)}</td>
                <td className={Number(x.delta) < 0 ? "neg" : Number(x.delta) > 0 ? "pos" : ""}>
                  {signedMoney(x.delta, c)} <span className="dim">{signedPct(x.delta_pct)}</span>
                </td>
                <td>
                  <span className={`pill pace ${x.state}`}>{STATE_LABEL[x.state]}</span>
                  {x.state === "stopped" && <span className="dim"> {stoppedSince(x)}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data.campaigns.length > shown.length || all ? (
        <button className="btn tiny" onClick={() => setAll((v) => !v)}>
          {all ? "Only what moved" : `All ${data.campaigns.length} campaigns`}
        </button>
      ) : null}
    </div>
  );
}
